/**
 * タイル定数定義
 * 仕様書: docs/reference/appendix_tile_list.md
 *
 * 14種類のタイル定義（接続情報、在庫数、名称）
 */

export type TileCategory = 'simple' | 'crossing' | 'coexist' | 'town';

export interface TileDefinition {
  /** タイルID（定数キー） */
  id: string;

  /** 表示名 */
  name: string;

  /** タイルカテゴリ */
  category: TileCategory;

  /**
   * 接続される辺の配列
   * - 単線: [[辺A, 辺B]]
   * - 複線: [[辺A, 辺B], [辺C, 辺D]]
   * - 町: [[辺A], [辺B], ...] (中心への接続)
   *
   * 辺の番号（Pointy-top orientation、data_dictionary.md準拠）:
   *        5  0
   *         \/
   *      4-[HEX]-1
   *         /\
   *        3  2
   *
   * - 0 = 北東 (NE, 30°)
   * - 1 = 東 (E, 90°)
   * - 2 = 南東 (SE, 150°)
   * - 3 = 南西 (SW, 210°)
   * - 4 = 西 (W, 270°)
   * - 5 = 北西 (NW, 330°)
   */
  connections: number[][];

  /** 町タイルフラグ */
  isTown: boolean;

  /** 新都市タイルフラグ（Urbanization専用） */
  isNewCity?: boolean;

  /** 新都市の色（新都市タイルの場合のみ） */
  newCityColor?: string;

  /** 初期在庫数 */
  stock: number;
}

/**
 * 全タイル定義（14種類）
 */
export const TILE_DEFINITIONS: Record<string, TileDefinition> = {
  // ========================================
  // Simple Tiles (3種)
  // ========================================

  STRAIGHT: {
    id: 'STRAIGHT',
    name: 'Straight',
    category: 'simple',
    connections: [[0, 3]],
    isTown: false,
    stock: 48
  },

  ACUTE_CURVE: {
    id: 'ACUTE_CURVE',
    name: 'Acute Curve',
    category: 'simple',
    connections: [[0, 1]],
    isTown: false,
    stock: 7
  },

  GENTLE_CURVE: {
    id: 'GENTLE_CURVE',
    name: 'Gentle Curve',
    category: 'simple',
    connections: [[0, 2]],
    isTown: false,
    stock: 55
  },

  // ========================================
  // Complex Crossing Tiles (3種)
  // ========================================

  GENTLE_STRAIGHT_CROSS: {
    id: 'GENTLE_STRAIGHT_CROSS',
    name: 'Gentle & Straight',
    category: 'crossing',
    connections: [[0, 3], [1, 5]],
    isTown: false,
    stock: 4
  },

  TWO_GENTLE_CROSS: {
    id: 'TWO_GENTLE_CROSS',
    name: 'Two Gentle',
    category: 'crossing',
    connections: [[0, 2], [1, 3]],
    isTown: false,
    stock: 3
  },

  TWO_STRAIGHT_CROSS: {
    id: 'TWO_STRAIGHT_CROSS',
    name: 'Two Straight',
    category: 'crossing',
    connections: [[0, 3], [1, 4]],
    isTown: false,
    stock: 4
  },

  // ========================================
  // Complex Coexist Tiles (4種)
  // ========================================

  TWO_GENTLE_COEXIST: {
    id: 'TWO_GENTLE_COEXIST',
    name: 'Gentle Curves',
    category: 'coexist',
    connections: [[0, 2], [3, 5]],
    isTown: false,
    stock: 1
  },

  LEFT_GENTLE_ACUTE: {
    id: 'LEFT_GENTLE_ACUTE',
    name: 'Left Gentle & Acute',
    category: 'coexist',
    connections: [[0, 2], [3, 4]],
    isTown: false,
    stock: 1
  },

  RIGHT_GENTLE_ACUTE: {
    id: 'RIGHT_GENTLE_ACUTE',
    name: 'Right Gentle & Acute',
    category: 'coexist',
    connections: [[0, 1], [2, 4]],
    isTown: false,
    stock: 1
  },

  STRAIGHT_ACUTE: {
    id: 'STRAIGHT_ACUTE',
    name: 'Straight & Acute',
    category: 'coexist',
    connections: [[0, 3], [4, 5]],
    isTown: false,
    stock: 1
  },

  // ========================================
  // Dedicated Town Tiles (5種)
  // ========================================

  TOWN_1: {
    id: 'TOWN_1',
    name: 'One Exit',
    category: 'town',
    connections: [[0]],
    isTown: true,
    stock: 3
  },

  TOWN_3_LEFT: {
    id: 'TOWN_3_LEFT',
    name: 'Three Exit Left',
    category: 'town',
    connections: [[0], [3], [4]],
    isTown: true,
    stock: 2
  },

  TOWN_3_RIGHT: {
    id: 'TOWN_3_RIGHT',
    name: 'Three Exit Right',
    category: 'town',
    connections: [[0], [2], [3]],
    isTown: true,
    stock: 2
  },

  TOWN_3_HALF: {
    id: 'TOWN_3_HALF',
    name: 'Three Exit Same Half',
    category: 'town',
    connections: [[0], [1], [2]],
    isTown: true,
    stock: 2
  },

  TOWN_3_STAR: {
    id: 'TOWN_3_STAR',
    name: 'Three Exit Star',
    category: 'town',
    connections: [[0], [2], [4]],
    isTown: true,
    stock: 2
  },

  // ========================================
  // New City Tiles (8種: A-H, 敷設後は都市と同じ扱い)
  // ========================================

  NEW_CITY_A: {
    id: 'NEW_CITY_A',
    name: 'New City A',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true, // 配置条件判定用（hex.townが必要）
    isNewCity: true,
    newCityColor: 'red',
    stock: 1
  },

  NEW_CITY_B: {
    id: 'NEW_CITY_B',
    name: 'New City B',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true,
    isNewCity: true,
    newCityColor: 'blue',
    stock: 1
  },

  NEW_CITY_C: {
    id: 'NEW_CITY_C',
    name: 'New City C',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true,
    isNewCity: true,
    newCityColor: 'black',
    stock: 1
  },

  NEW_CITY_D: {
    id: 'NEW_CITY_D',
    name: 'New City D',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true,
    isNewCity: true,
    newCityColor: 'black',
    stock: 1
  },

  NEW_CITY_E: {
    id: 'NEW_CITY_E',
    name: 'New City E',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true,
    isNewCity: true,
    newCityColor: 'yellow',
    stock: 1
  },

  NEW_CITY_F: {
    id: 'NEW_CITY_F',
    name: 'New City F',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true,
    isNewCity: true,
    newCityColor: 'purple',
    stock: 1
  },

  NEW_CITY_G: {
    id: 'NEW_CITY_G',
    name: 'New City G',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true,
    isNewCity: true,
    newCityColor: 'black',
    stock: 1
  },

  NEW_CITY_H: {
    id: 'NEW_CITY_H',
    name: 'New City H',
    category: 'town',
    connections: [[0], [1], [2], [3], [4], [5]], // 都市と同じ（全辺接続可能）
    isTown: true,
    isNewCity: true,
    newCityColor: 'black',
    stock: 1
  }
};

/**
 * 全タイルIDの配列（BuildTrackPhaseで使用）
 */
export const ALL_TILE_IDS = Object.keys(TILE_DEFINITIONS);

/**
 * タイルIDからTileDefinitionを取得
 */
export function getTileDefinition(tileId: string): TileDefinition | undefined {
  return TILE_DEFINITIONS[tileId];
}

/**
 * カテゴリ別のタイルIDリスト
 */
export const TILE_IDS_BY_CATEGORY: Record<TileCategory, string[]> = {
  simple: ['STRAIGHT', 'ACUTE_CURVE', 'GENTLE_CURVE'],
  crossing: ['GENTLE_STRAIGHT_CROSS', 'TWO_GENTLE_CROSS', 'TWO_STRAIGHT_CROSS'],
  coexist: ['TWO_GENTLE_COEXIST', 'LEFT_GENTLE_ACUTE', 'RIGHT_GENTLE_ACUTE', 'STRAIGHT_ACUTE'],
  town: ['TOWN_1', 'TOWN_3_LEFT', 'TOWN_3_RIGHT', 'TOWN_3_HALF', 'TOWN_3_STAR']
};
